import React from "react";
import Head from "next/head";
import { COMPANY_NAME, COMPANY_TITLE } from "@/constants/common";

interface Props {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  ogTitle?: string;
  ogDescription?: string;
  hreflangs?: Array<{
    locale: string;
    url: string;
  }>;
  canonical?: string;
}

export const Seo: React.FC<Props> = ({
  title: tmpTitle = "Prémiový Rezervačný Systém na Mieru pre Vaše Podnikanie",
  description = "Objavte Flexibooking.sk, prispôsobiteľný rezervačný systém, ktorý ponúka unikátny dizajn a flexibilitu funkcionality. Zvýšte efektivitu a zlepšite zákaznícku skúsenosť s naším prémiovým riešením.",
  url = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk",
  image = "/og_image.png",
  ogTitle,
  ogDescription,
  hreflangs = [],
  canonical,
}) => {
  const title = [
    ...(tmpTitle ? [tmpTitle] : []),
    COMPANY_TITLE
  ].join(' | ')
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;

  // Ensure image URL is absolute
  const baseUrl = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk";
  const absoluteImage = image.startsWith("http") ? image : `${baseUrl}${image}`;

  return (
    <Head>
      <meta charSet="utf-8" className="next-head" />

      {/* Twitter Card data */}
      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      <meta name="twitter:title" content={title} key="twitter:title" />
      <meta
        name="twitter:description"
        content={description}
        key="twitter:description"
      />
      <meta name="twitter:image" content={absoluteImage} key="twitter:image" />

      {/* Open Graph data */}
      <meta property="og:title" content={finalOgTitle} key="og:title" />
      <meta property="og:type" content="website" key="og:type" />
      <meta property="og:url" content={url} key="og:url" />
      <meta property="og:image" content={absoluteImage} key="og:image" />
      <meta
        property="og:description"
        content={finalOgDescription}
        key="og:description"
      />
      <meta property="og:site_name" content={title} className="next-head" />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} key="canonical" />}

      {/* Hreflang tags for multilingual support */}
      {hreflangs.length > 0 && (
        <>
          {/* x-default for language fallback (primary locale: English) */}
          <link
            key="hreflang-x-default"
            rel="alternate"
            hrefLang="x-default"
            href={hreflangs.find(h => h.locale === 'en')?.url || hreflangs[0]?.url}
          />
          {hreflangs.map((hreflang) => (
            <link
              key={`hreflang-${hreflang.locale}`}
              rel="alternate"
              hrefLang={hreflang.locale}
              href={hreflang.url}
            />
          ))}
        </>
      )}

      <title key="title">{title}</title>
      <link
        rel="icon"
        type="image/x-icon"
        href="/favicon.ico"
      />
      <meta name="author" content={COMPANY_NAME} className="next-head" />
      <meta name="googlebot" content="follow" className="next-head" />
      <meta name="robots" content="index, follow" className="next-head" />
      <meta name="description" content={description} className="next-head" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0"
        key="viewport"
      />
    </Head>
  );
}
